import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { errorText, periodLabel, recentQuarters } from './questShared';

// Pieces shared by the Nano Quest admin screens (Communications, in Nano Dash).
// The employee side — taking Quests, the leaderboard — is in the FSM app.

export const BTN = 'inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-dark disabled:opacity-40';
export const BTN_SECONDARY = 'inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40';
export const BTN_DANGER = 'inline-flex items-center justify-center gap-1.5 rounded-lg border border-rose-200 bg-white px-4 py-2 text-sm font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-40';
export const INPUT = 'block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand';
// A filter dropdown that is only as wide as what it shows (INPUT is full width).
export const FILTER = 'rounded-md border border-gray-300 bg-white py-1.5 pl-3 pr-8 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand';
export const LABEL = 'mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500';

export function AdminPage({ title, back = '/quest', backLabel = 'Nano Quest', actions, children }) {
  return (
    <div className="p-6">
      <div className="mx-auto max-w-6xl">
        <Link to={back} className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-brand hover:text-brand-dark">
          <ChevronLeftIcon className="h-4 w-4" />
          {backLabel}
        </Link>
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <h1 className="text-2xl font-bold tracking-tight text-gray-900">{title}</h1>
          {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
        </div>
        {children}
      </div>
    </div>
  );
}

// Their Microsoft 365 photo, or their initials when they have none (or it
// hasn't come yet) — as on the app's leaderboard.
const initials = (name) => (name || '?').split(/\s+/).filter(Boolean).slice(0, 2)
  .map((w) => w[0].toUpperCase()).join('');
export function Avatar({ name, photo, size = 'h-8 w-8 text-xs' }) {
  if (photo) return <img src={photo} alt="" className={`${size} shrink-0 rounded-full bg-gray-200 object-cover`} />;
  return (
    <span className={`flex ${size} shrink-0 items-center justify-center rounded-full bg-brand font-bold text-white`}>
      {initials(name)}
    </span>
  );
}

// The brief's department: a placeholder until there is a source for one (see
// the tech server's services/questPeople.js).
export const DEPARTMENT_SOON = 'Department not recorded yet';

export const Card = ({ children, className = '' }) => (
  <div className={`rounded-lg border border-gray-200 bg-white ${className}`}>{children}</div>
);

// An expandable section, as on the other Nano Dash dashboards (the Absence
// dashboard's Collapsible): chevron and title on the left, a count on the
// right, and an optional action (a CSV download, say) beside it that is not
// part of the toggle. defaultOpen: whether it starts open.
export function Section({
  title, description, badge, action, defaultOpen = false, children,
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
          className="flex min-w-0 flex-1 items-start gap-1.5 text-left">
          {open
            ? <ChevronDownIcon className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
            : <ChevronRightIcon className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />}
          <span className="min-w-0">
            <span className="block text-sm font-semibold text-gray-900">{title}</span>
            {description ? <span className="block text-xs text-gray-400">{description}</span> : null}
          </span>
        </button>
        <span className="flex shrink-0 items-center gap-2">
          {action}
          {badge != null ? (
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-500">{badge}</span>
          ) : null}
        </span>
      </div>
      {open ? <div className="border-t border-gray-100">{children}</div> : null}
    </section>
  );
}

// A small CSV download for a section header.
export function CsvButton({ onClick, disabled, label = 'CSV' }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled}
      className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-gray-600 hover:border-brand hover:text-brand disabled:opacity-40">
      <svg viewBox="0 0 20 20" fill="currentColor" className="h-3.5 w-3.5" aria-hidden="true">
        <path d="M10.75 2.75a.75.75 0 0 0-1.5 0v8.614L6.295 8.235a.75.75 0 1 0-1.09 1.03l4.25 4.5a.75.75 0 0 0 1.09 0l4.25-4.5a.75.75 0 0 0-1.09-1.03l-2.955 3.129V2.75Z" />
        <path d="M3.5 12.75a.75.75 0 0 0-1.5 0v2.5A2.75 2.75 0 0 0 4.75 18h10.5A2.75 2.75 0 0 0 18 15.25v-2.5a.75.75 0 0 0-1.5 0v2.5c0 .69-.56 1.25-1.25 1.25H4.75c-.69 0-1.25-.56-1.25-1.25v-2.5Z" />
      </svg>
      {label}
    </button>
  );
}

export function Loading({ q, what }) {
  if (q.isLoading) return <p className="px-4 py-6 text-sm text-gray-400">Loading…</p>;
  if (q.isError) return <ErrorNote error={q.error} fallback={`Could not load ${what}.`} />;
  return null;
}

export function ErrorNote({ error, fallback = 'That did not save.' }) {
  if (!error) return null;
  return (
    <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
      {errorText(error, fallback)}
    </p>
  );
}

const STATE = {
  draft: ['Draft', 'bg-gray-100 text-gray-600'],
  scheduled: ['Scheduled', 'bg-amber-50 text-amber-700'],
  live: ['Live', 'bg-emerald-50 text-emerald-700'],
  closed: ['Closed', 'bg-gray-100 text-gray-500'],
};

export function StateBadge({ state }) {
  const [label, cls] = STATE[state] || [state, 'bg-gray-100 text-gray-600'];
  return <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold ${cls}`}>{label}</span>;
}

// A quarter or the whole year. value: "2026-Q4" | "2026".
// Which stretch of time to show: a quarter (points reset each quarter), or a
// whole year, each named in full (periodLabel).
export function PeriodSelect({ value, onChange, withYears = true }) {
  const quarters = recentQuarters(8);
  const years = [...new Set(quarters.map((q) => q.slice(0, 4)))];
  return (
    <label className="flex items-center gap-2 text-sm text-gray-600">
      <span className="font-medium">Period</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} className={FILTER}>
        {quarters.map((q, i) => (
          <option key={q} value={q}>{periodLabel(q)}{i === 0 ? ' (now)' : ''}</option>
        ))}
        {withYears ? years.map((y) => <option key={y} value={y}>All of {y}</option>) : null}
      </select>
    </label>
  );
}

export function Stat({ label, value, sub }) {
  return (
    <Card className="px-4 py-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</p>
      <p className="mt-1 text-2xl font-bold tabular-nums text-gray-900">{value}</p>
      {sub ? <p className="mt-0.5 text-xs text-gray-400">{sub}</p> : null}
    </Card>
  );
}
